/**
 * KHIYARSA (Phase 2): cloud sync engine for private projects.
 *
 * Design:
 * - Local-first: the app's own localStorage/IndexedDB autosave (LocalData)
 *   is untouched; this engine additionally pushes the scene to the server,
 *   debounced, whenever a project is "attached".
 * - Conflict-safe: every save carries the server content_revision it was
 *   based on; the server rejects stale saves with 409 and the user chooses
 *   how to resolve (both choices preserve the losing version in history).
 * - The attachment (which project this browser's canvas belongs to) is
 *   persisted in localStorage so closing/reopening the browser reconnects
 *   and syncs any offline edits.
 */
import {
  CaptureUpdateAction,
  getSceneVersion,
  exportToBlob,
} from "@excalidraw/excalidraw";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";

import type {
  BinaryFileData,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { FileId } from "@excalidraw/element/types";

import { appJotaiStore } from "../../app-jotai";
import { collabAPIAtom } from "../../collab/Collab";

import { ApiError, cloudApi } from "./api";
import {
  attachedProjectAtom,
  cloudConflictAtom,
  cloudUserAtom,
  syncStatusAtom,
} from "./atoms";

import type { CloudUser, ProjectMeta } from "./api";
import type { AttachedProject } from "./atoms";

const ATTACH_KEY = "khiyarsa-cloud-attach-v1";
const DEBOUNCE_MS = 3000;
const MAX_WAIT_MS = 15_000;
const RETRY_MS = 10_000;
const THUMBNAIL_INTERVAL_MS = 60_000;
const THUMBNAIL_MAX_CHARS = 140_000;

const readStoredAttach = (): AttachedProject | null => {
  try {
    const raw = localStorage.getItem(ATTACH_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed?.projectId !== "string") {
      return null;
    }
    return parsed as AttachedProject;
  } catch {
    return null;
  }
};

class CloudSyncEngine {
  private excalidrawAPI: ExcalidrawImperativeAPI | null = null;
  private user: CloudUser | null = null;
  private attach: AttachedProject | null = null;

  private dirtyTimer: ReturnType<typeof setTimeout> | null = null;
  private firstDirtyAt: number | null = null;
  private pushing = false;
  private pendingAfterPush = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  // per-project throttle — switching projects must not suppress the other
  // project's thumbnail refresh
  private lastThumbnailAt = new Map<string, number>();
  private defaultTitle = typeof document !== "undefined" ? document.title : "";
  private initialized = false;

  // Resolved by App.tsx once initializeScene has delivered the initial scene;
  // reconciliation must not read the canvas before that (an empty pre-load
  // canvas must never be mistaken for "the user cleared the canvas").
  private sceneReadyResolve: (() => void) | null = null;
  private sceneReady = new Promise<void>((resolve) => {
    this.sceneReadyResolve = resolve;
  });
  private lastChangeAt = 0;

  notifySceneReady() {
    this.sceneReadyResolve?.();
  }

  private async waitForCanvas() {
    await Promise.race([
      this.sceneReady,
      new Promise((resolve) => setTimeout(resolve, 8000)),
    ]);
    // initialData application is async inside the editor — wait until a
    // change event lands (the editor commits the loaded scene) or briefly.
    const readyAt = Date.now();
    for (let i = 0; i < 20 && this.lastChangeAt < readyAt; i++) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // -------------------------------------------------------------------------
  // lifecycle
  // -------------------------------------------------------------------------

  init(api: ExcalidrawImperativeAPI) {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.excalidrawAPI = api;
    api.onChange(() => this.handleChange());
    window.addEventListener("online", () => this.push());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.firstDirtyAt != null) {
        this.push();
      }
    });
    window.addEventListener("storage", (event) => {
      if (event.key === ATTACH_KEY) {
        const stored = readStoredAttach();
        if (JSON.stringify(stored) !== JSON.stringify(this.attach)) {
          this.attach = stored;
          appJotaiStore.set(attachedProjectAtom, stored);
          this.applyTitle();
        }
      }
    });
    this.bootstrap();
  }

  private async bootstrap() {
    // A transient failure (backend restart, nginx 429/5xx, flaky network)
    // must not strand a signed-in user in a signed-out, non-syncing state:
    // retry with backoff until we get a definitive answer (user or 401).
    const delays = [5_000, 15_000, 30_000, 60_000, 120_000];
    for (let attempt = 0; ; attempt++) {
      try {
        const user = await cloudApi.me();
        this.setUser(user);
        if (user) {
          await this.waitForCanvas();
          await this.reconcile();
        }
        return;
      } catch {
        if (attempt === 0) {
          // keep the UI usable (menu shows "Sign in") while we retry
          appJotaiStore.set(cloudUserAtom, null);
        }
        if (attempt >= delays.length) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      }
    }
  }

  // -------------------------------------------------------------------------
  // state helpers
  // -------------------------------------------------------------------------

  setUser(user: CloudUser | null) {
    this.user = user;
    appJotaiStore.set(cloudUserAtom, user);
    if (!user) {
      this.setStatus("idle");
    }
  }

  private setAttach(attach: AttachedProject | null) {
    this.attach = attach;
    appJotaiStore.set(attachedProjectAtom, attach);
    try {
      if (attach) {
        localStorage.setItem(ATTACH_KEY, JSON.stringify(attach));
      } else {
        localStorage.removeItem(ATTACH_KEY);
      }
    } catch {
      // quota errors — non-fatal
    }
    this.applyTitle();
  }

  private applyTitle() {
    document.title = this.attach
      ? `${this.attach.title} — khiyarsa`
      : this.defaultTitle;
  }

  private setStatus(status: "idle" | "synced" | "saving" | "offline" | "conflict") {
    appJotaiStore.set(syncStatusAtom, status);
  }

  private setConflict(project: ProjectMeta | null) {
    appJotaiStore.set(
      cloudConflictAtom,
      project
        ? {
            serverRevision: project.contentRevision,
            serverUpdatedAt: project.updatedAt,
          }
        : null,
    );
    if (project) {
      this.setStatus("conflict");
    }
  }

  getAttach() {
    return this.attach;
  }

  isCollaborating() {
    return appJotaiStore.get(collabAPIAtom)?.isCollaborating() === true;
  }

  // -------------------------------------------------------------------------
  // change tracking / debounced autosave
  // -------------------------------------------------------------------------

  private handleChange() {
    this.lastChangeAt = Date.now();
    if (
      !this.user ||
      !this.attach ||
      appJotaiStore.get(cloudConflictAtom) ||
      this.isCollaborating()
    ) {
      return;
    }
    this.markDirty();
  }

  private markDirty() {
    if (this.firstDirtyAt == null) {
      this.firstDirtyAt = Date.now();
    }
    if (this.dirtyTimer) {
      clearTimeout(this.dirtyTimer);
    }
    const overdue = Date.now() - this.firstDirtyAt > MAX_WAIT_MS;
    this.dirtyTimer = setTimeout(() => this.push(), overdue ? 0 : DEBOUNCE_MS);
  }

  private scheduleRetry() {
    this.setStatus("offline");
    if (this.retryTimer) {
      return;
    }
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.push();
    }, RETRY_MS);
  }

  private currentScene() {
    const api = this.excalidrawAPI!;
    const elements = api.getSceneElements();
    return {
      elements,
      sceneVersion: getSceneVersion(elements),
      appState: clearAppStateForLocalStorage(api.getAppState()) as Record<
        string,
        any
      >,
    };
  }

  private async maybeThumbnail(
    projectId: string,
    opts: { force?: boolean } = {},
  ): Promise<string | undefined> {
    const api = this.excalidrawAPI!;
    if (
      !opts.force &&
      Date.now() - (this.lastThumbnailAt.get(projectId) ?? 0) <
        THUMBNAIL_INTERVAL_MS
    ) {
      return undefined;
    }
    const elements = api.getSceneElements();
    if (elements.length === 0) {
      return undefined;
    }
    try {
      const blob = await exportToBlob({
        elements,
        appState: { ...api.getAppState(), exportBackground: true },
        files: api.getFiles(),
        maxWidthOrHeight: 320,
        mimeType: "image/png",
      });
      const dataURL = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      if (dataURL.length > THUMBNAIL_MAX_CHARS) {
        return undefined;
      }
      this.lastThumbnailAt.set(projectId, Date.now());
      return dataURL;
    } catch {
      return undefined;
    }
  }

  private async uploadMissingFiles(projectId: string, missing: string[]) {
    if (missing.length === 0) {
      return;
    }
    const files = this.excalidrawAPI!.getFiles();
    for (const fileId of missing) {
      const file = files[fileId as FileId];
      if (file?.dataURL) {
        try {
          await cloudApi.uploadFile(projectId, fileId, file.dataURL);
        } catch (error) {
          console.warn(`[khiyarsa-cloud] file upload failed: ${fileId}`, error);
        }
      }
    }
  }

  async push(opts: { force?: boolean } = {}) {
    if (this.dirtyTimer) {
      clearTimeout(this.dirtyTimer);
      this.dirtyTimer = null;
    }
    if (!this.excalidrawAPI || !this.user || !this.attach) {
      return;
    }
    if (!opts.force && appJotaiStore.get(cloudConflictAtom)) {
      return;
    }
    if (this.pushing) {
      this.pendingAfterPush = true;
      return;
    }
    const attach = this.attach;
    const { elements, sceneVersion, appState } = this.currentScene();
    if (!opts.force && sceneVersion === attach.sceneVersion) {
      this.firstDirtyAt = null;
      this.setStatus("synced");
      return;
    }
    this.pushing = true;
    this.setStatus("saving");
    try {
      const thumbnail = await this.maybeThumbnail(attach.projectId);
      const res = await cloudApi.saveProject(attach.projectId, {
        baseRevision: opts.force ? undefined : attach.revision,
        force: opts.force || undefined,
        sceneVersion,
        scene: { elements: elements as unknown as any[], appState },
        thumbnail,
      });
      await this.uploadMissingFiles(attach.projectId, res.missingFiles);
      this.firstDirtyAt = null;
      this.setAttach({ ...attach, revision: res.revision, sceneVersion });
      this.setConflict(null);
      this.setStatus("synced");
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await this.handleConflict();
      } else if (error instanceof ApiError && error.status === 401) {
        // session expired
        this.setUser(null);
        this.setStatus("idle");
      } else if (error instanceof ApiError && error.status === 404) {
        // project deleted elsewhere — detach, keep canvas + local autosave
        this.detach();
      } else {
        this.scheduleRetry();
      }
    } finally {
      this.pushing = false;
      if (this.pendingAfterPush) {
        this.pendingAfterPush = false;
        this.markDirty();
      }
    }
  }

  private async handleConflict() {
    const attach = this.attach;
    if (!attach) {
      return;
    }
    try {
      const { project } = await cloudApi.getProject(attach.projectId);
      const localVersion = getSceneVersion(this.excalidrawAPI!.getSceneElements());
      if (
        project.sceneVersion != null &&
        project.sceneVersion === localVersion
      ) {
        // same content raced in from another tab of the same browser —
        // adopt the server revision silently.
        this.setAttach({
          ...attach,
          title: project.title,
          revision: project.contentRevision,
          sceneVersion: localVersion,
        });
        this.setStatus("synced");
        return;
      }
      this.setConflict(project);
    } catch {
      this.scheduleRetry();
    }
  }

  // -------------------------------------------------------------------------
  // startup reconciliation (cross-device / offline continuation)
  // -------------------------------------------------------------------------

  private async reconcile() {
    const stored = readStoredAttach();
    if (!stored || !this.excalidrawAPI) {
      return;
    }
    let res;
    try {
      res = await cloudApi.getProject(stored.projectId);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 401)) {
        this.setAttach(null);
      } else {
        // offline: attach optimistically; pushes will retry
        this.setAttach(stored);
        this.setStatus("offline");
      }
      return;
    }
    const localVersion = getSceneVersion(this.excalidrawAPI.getSceneElements());
    // Safety guard: an empty local canvas while the stored sync point was
    // non-empty means local state was lost or not loaded (cleared storage,
    // load race) — prefer pulling the cloud copy over pushing emptiness.
    if (
      localVersion === 0 &&
      stored.sceneVersion !== 0 &&
      res.scene != null
    ) {
      await this.loadIntoCanvas(res);
      return;
    }
    if (res.project.contentRevision === stored.revision) {
      this.setAttach({ ...stored, title: res.project.title });
      if (localVersion !== stored.sceneVersion) {
        this.markDirty(); // offline edits → push
      } else {
        this.setStatus("synced");
      }
    } else if (localVersion === stored.sceneVersion) {
      // canvas untouched since last sync; fast-forward to the newer cloud copy
      await this.loadIntoCanvas(res);
    } else {
      this.setAttach({ ...stored, title: res.project.title });
      this.setConflict(res.project);
    }
  }

  // -------------------------------------------------------------------------
  // project operations (called from UI)
  // -------------------------------------------------------------------------

  private async loadIntoCanvas(res: {
    project: ProjectMeta;
    scene: { elements: any[]; appState: Record<string, any> } | null;
    files: { id: string; mimeType: string; size: number }[];
  }) {
    const api = this.excalidrawAPI!;
    const scene = res.scene ?? { elements: [], appState: {} };
    const elements = restoreElements(scene.elements, null, {
      repairBindings: true,
    });
    const appState = restoreAppState(scene.appState || {}, null);
    // A project switch must not be undoable: Ctrl+Z would resurrect the
    // previous project's content on the canvas and autosave would then push
    // it into the newly attached project. Mirror the editor's own scene
    // initialization: clear history first, apply with NEVER so the load
    // itself is not captured as an undoable increment.
    api.history.clear();
    api.updateScene({
      elements,
      appState: { ...appState, isLoading: false },
      captureUpdate: CaptureUpdateAction.NEVER,
    });

    const known = api.getFiles();
    const toFetch = res.files.filter((f) => !known[f.id as FileId]);
    const loaded: BinaryFileData[] = [];
    await Promise.all(
      toFetch.map(async (f) => {
        const file = await cloudApi.downloadFile(res.project.id, f.id);
        if (file) {
          loaded.push({
            id: f.id as FileId,
            dataURL: file.dataURL,
            mimeType: (file.mimeType || f.mimeType) as BinaryFileData["mimeType"],
            created: Date.now(),
          } as BinaryFileData);
        }
      }),
    );
    if (loaded.length > 0) {
      api.addFiles(loaded);
    }

    this.setConflict(null);
    this.setAttach({
      projectId: res.project.id,
      title: res.project.title,
      revision: res.project.contentRevision,
      sceneVersion: getSceneVersion(api.getSceneElements()),
    });
    this.firstDirtyAt = null;
    this.setStatus("synced");
  }

  async openProject(projectId: string) {
    // flush pending changes of the currently attached project first
    if (this.attach && this.firstDirtyAt != null) {
      await this.push();
    }
    const res = await cloudApi.getProject(projectId);
    await this.loadIntoCanvas(res);
  }

  /** True if opening a project would clobber an unsynced, non-empty canvas. */
  wouldClobberCanvas() {
    if (!this.excalidrawAPI || this.attach) {
      return false;
    }
    return this.excalidrawAPI.getSceneElements().length > 0;
  }

  async createProjectFromCanvas(title: string): Promise<ProjectMeta> {
    const { elements, sceneVersion, appState } = this.currentScene();
    const thumbnail = await this.maybeThumbnail("new-project", { force: true });
    const res = await cloudApi.createProject({
      title,
      scene: { elements: elements as unknown as any[], appState },
      sceneVersion,
      thumbnail,
    });
    await this.uploadMissingFiles(res.project.id, res.missingFiles);
    this.setConflict(null);
    this.setAttach({
      projectId: res.project.id,
      title: res.project.title,
      revision: res.project.contentRevision,
      sceneVersion,
    });
    this.setStatus("synced");
    return res.project;
  }

  async createBlankProject(title: string): Promise<ProjectMeta> {
    const res = await cloudApi.createProject({ title });
    await this.openProject(res.project.id);
    return res.project;
  }

  async resolveConflictKeepMine() {
    await this.push({ force: true });
  }

  async resolveConflictUseCloud() {
    if (!this.attach) {
      return;
    }
    const res = await cloudApi.getProject(this.attach.projectId);
    await this.loadIntoCanvas(res);
  }

  async resolveConflictSaveCopy() {
    if (!this.attach) {
      return;
    }
    const original = this.attach;
    const { elements, sceneVersion, appState } = this.currentScene();
    const thumbnail = await this.maybeThumbnail("conflict-copy", {
      force: true,
    });
    const copy = await cloudApi.createProject({
      title: `${original.title} (copy)`,
      scene: { elements: elements as unknown as any[], appState },
      sceneVersion,
      thumbnail,
    });
    await this.uploadMissingFiles(copy.project.id, copy.missingFiles);
    // re-point at the original and pull its cloud version
    this.attach = original;
    const res = await cloudApi.getProject(original.projectId);
    await this.loadIntoCanvas(res);
  }

  async restoreRevision(projectId: string, revisionId: number) {
    // the stored thumbnail now shows pre-restore content — refresh on the
    // next save instead of waiting out the throttle
    this.lastThumbnailAt.delete(projectId);
    const res = await cloudApi.restoreRevision(projectId, revisionId);
    if (this.attach?.projectId === projectId) {
      const full = await cloudApi.getProject(projectId);
      await this.loadIntoCanvas(full);
    }
    return res;
  }

  onProjectRenamed(projectId: string, title: string) {
    if (this.attach?.projectId === projectId) {
      this.setAttach({ ...this.attach, title });
    }
  }

  onProjectDeleted(projectId: string) {
    if (this.attach?.projectId === projectId) {
      this.detach();
    }
  }

  detach() {
    if (this.dirtyTimer) {
      clearTimeout(this.dirtyTimer);
      this.dirtyTimer = null;
    }
    this.firstDirtyAt = null;
    this.setConflict(null);
    this.setAttach(null);
    this.setStatus("idle");
  }

  async signOut() {
    try {
      await cloudApi.logout();
    } catch {
      // clearing local state matters more than the server call
    }
    this.detach();
    this.setUser(null);
  }
}

export const cloudSync = new CloudSyncEngine();
